// Design My Own composer tools for the web (LAN-716 / LAN-717): the
// Dictate · Enhance row under the prompt textarea, the "✕ Clear" control
// inside it, and — after an enhancement is applied — the
// "Show original | Revert to original" links.
//
// Mirrors languatalk-app/components/speak/PromptComposer.tsx. Used twice by
// chat_topic_service.js: the creation composer (source 'create') and the My
// Prompts edit modal (source 'edit'). The textarea stays the single source of
// truth — every change goes through it and triggers 'input', so the existing
// character counters and Start chat / Save wiring keep working untouched.
//
// Depends on the page globals `$` (jQuery) and `I18n`, like the rest of the
// chat scripts.

import AudioRecorder from 'audio-recorder-polyfill';
import { acquireMicStream } from './mic_capture';
import {
  DICTATION_TIMEOUT_MS,
  ENHANCEMENT_TIMEOUT_MS,
  MAX_RECORDING_MS,
  PROMPT_MAX_CHARS,
  appendTranscript,
  clampToMaxChars,
  enhancementErrorKey,
  parseSseTranscript,
  shouldEnableEnhance,
} from './prompt_composer_helpers';

const t = (key, opts) => I18n.t(key, opts);

export function composerToolsHtml() {
  return `
    <div class="prompt-composer-tools">
      <div class="prompt-composer-actions js-prompt-composer-actions">
        <button type="button" class="btn prompt-composer-action js-prompt-dictate" aria-pressed="false">
          <i class="fa fa-microphone" aria-hidden="true"></i><span class="js-prompt-dictate-label">${t('javascript.chat.dictate')}</span>
        </button>
        <button type="button" class="btn prompt-composer-action js-prompt-enhance" disabled>
          <i class="fa fa-magic" aria-hidden="true"></i><span class="js-prompt-enhance-label">${t('javascript.chat.enhance')}</span>
        </button>
      </div>
      <div class="alert alert-danger prompt-composer-error d-none js-prompt-tools-error" role="alert">
        <span class="js-prompt-tools-error-text"></span>
        <button type="button" class="close js-prompt-tools-error-dismiss" aria-label="Close"><span aria-hidden="true">&times;</span></button>
      </div>
      <div class="prompt-composer-original-links d-none js-prompt-original-links">
        <a href="#" class="js-prompt-toggle-original">${t('javascript.chat.show_original')}</a>
        <span class="prompt-composer-original-sep" aria-hidden="true">|</span>
        <a href="#" class="js-prompt-revert-original">${t('javascript.chat.revert_to_original')}</a>
      </div>
      <div class="prompt-composer-original d-none js-prompt-original"></div>
    </div>`;
}

export default class PromptComposerTools {
  /**
   * @param {object} options
   * @param {HTMLElement} options.mount     empty element to render the tools into (placed below the textarea's form-group)
   * @param {HTMLTextAreaElement} options.textarea the prompt field
   * @param {'create'|'edit'} options.source analytics only
   * @param {string} options.nativeLanguage plain-name transcription hint ('' = auto-detect)
   * @param {(busy: boolean) => void} [options.onBusyChange] recording, transcribing or enhancing
   */
  constructor({ mount, textarea, source, nativeLanguage = '', onBusyChange }) {
    this.$mount = $(mount);
    this.$textarea = $(textarea);
    this.source = source;
    this.nativeLanguage = nativeLanguage || '';
    this.onBusyChange = onBusyChange || (() => {});

    this.isAcquiringMic = false;
    this.isRecording = false;
    this.isTranscribing = false;
    this.isEnhancing = false;
    // The draft as it was when Enhance was pressed; '' when nothing has been
    // applied yet. Drives the "Show original | Revert to original" links.
    this.originalDraft = '';
    this.enhanceRequestId = 0;
    // Bumped on every cancel/reset/destroy. A mic acquisition, recorder stop
    // event or transcription that finishes under an older generation is
    // discarded, so nothing from a closed composer can reach a reopened one.
    this.dictationGeneration = 0;
    this.transcribeController = null;
    this.recordingTimer = null;

    this.render();
    this.bind();
    this.syncState();
  }

  // ---------------------------------------------------------------- render

  render() {
    // Clear pill inside the field: wrap the textarea so it can be absolutely
    // positioned in its bottom-right corner.
    if (!this.$textarea.parent().hasClass('prompt-composer-field')) {
      this.$textarea.wrap('<div class="prompt-composer-field"></div>');
    }
    this.$field = this.$textarea.parent();
    this.$field.find('.js-prompt-clear').remove();
    this.$clear = $(`
      <button type="button" class="btn btn-sm prompt-composer-clear js-prompt-clear d-none" aria-label="${t('javascript.chat.clear_prompt')}">
        <i class="fa fa-times" aria-hidden="true"></i><span>${t('javascript.chat.clear_prompt')}</span>
      </button>`);
    this.$field.append(this.$clear);

    this.$mount.html(composerToolsHtml());
    this.$actions = this.$mount.find('.js-prompt-composer-actions');
    this.$dictate = this.$mount.find('.js-prompt-dictate');
    this.$dictateLabel = this.$mount.find('.js-prompt-dictate-label');
    this.$enhance = this.$mount.find('.js-prompt-enhance');
    this.$enhanceLabel = this.$mount.find('.js-prompt-enhance-label');
    this.$error = this.$mount.find('.js-prompt-tools-error');
    this.$errorText = this.$mount.find('.js-prompt-tools-error-text');
    this.$originalLinks = this.$mount.find('.js-prompt-original-links');
    this.$toggleOriginal = this.$mount.find('.js-prompt-toggle-original');
    this.$revertOriginal = this.$mount.find('.js-prompt-revert-original');
    this.$original = this.$mount.find('.js-prompt-original');
  }

  bind() {
    this.$textarea.on('input.promptComposerTools', () => this.syncState());
    this.$clear.on('click', () => {
      this.setDraft('');
      this.$textarea.trigger('focus');
    });
    this.$enhance.on('click', () => this.enhance());
    this.$dictate.on('click', () => (this.isRecording ? this.stopRecording() : this.startRecording()));
    this.$mount.on('click', '.js-prompt-tools-error-dismiss', () => this.hideError());
    this.$toggleOriginal.on('click', (e) => {
      e.preventDefault();
      const show = this.$original.hasClass('d-none');
      this.$original.toggleClass('d-none', !show);
      this.$toggleOriginal.text(t(show ? 'javascript.chat.hide_original' : 'javascript.chat.show_original'));
    });
    this.$revertOriginal.on('click', (e) => {
      e.preventDefault();
      this.revertToOriginal();
    });
  }

  // Drop transient state (applied-enhancement links, error, an in-flight
  // request's result) and stop any dictation — used when the edit modal is
  // opened for a different prompt or closed.
  reset() {
    this.cancelDictation();
    this.enhanceRequestId += 1;
    this.isEnhancing = false;
    this.clearOriginal();
    this.hideError();
    this.syncState();
  }

  // Called when the composer's markup is being thrown away (navigation, view
  // re-render). Releases the microphone and detaches from the textarea.
  destroy() {
    this.cancelDictation();
    this.enhanceRequestId += 1;
    this.isEnhancing = false;
    this.$textarea.off('.promptComposerTools');
    this.$clear.remove();
    this.$mount.empty();
    this.onBusyChange = () => {};
  }

  // ----------------------------------------------------------------- state

  get draft() {
    return this.$textarea.val() || '';
  }

  get isDictating() {
    return this.isAcquiringMic || this.isRecording || this.isTranscribing;
  }

  get isBusy() {
    return this.isDictating || this.isEnhancing;
  }

  // Every programmatic write is clamped: `.val()` ignores the HTML maxlength.
  setDraft(value) {
    this.$textarea.val(clampToMaxChars(value, PROMPT_MAX_CHARS)).trigger('input');
  }

  syncState() {
    const draft = this.draft;
    this.$clear.toggleClass('d-none', draft.length === 0 || this.isEnhancing);
    this.$field.toggleClass('has-clear', draft.length > 0 && !this.isEnhancing);
    this.$textarea.prop('readonly', this.isEnhancing);
    this.$enhance.prop('disabled', this.isEnhancing || this.isDictating || !shouldEnableEnhance(draft));
    this.$dictate.prop('disabled', this.isEnhancing || this.isTranscribing || this.isAcquiringMic);
    this.$enhanceLabel.text(t(this.isEnhancing ? 'javascript.chat.enhancing' : 'javascript.chat.enhance'));
    this.$enhance.find('i').attr('class', this.isEnhancing ? 'fa fa-spinner fa-spin' : 'fa fa-magic');
    this.$dictate.toggleClass('is-recording', this.isRecording).attr('aria-pressed', String(this.isRecording));
    this.$dictateLabel.text(
      t(this.isTranscribing ? 'javascript.chat.transcribing' : this.isRecording ? 'javascript.chat.dictate_stop' : 'javascript.chat.dictate'),
    );
    this.$dictate.find('i').attr(
      'class',
      this.isTranscribing ? 'fa fa-spinner fa-spin' : this.isRecording ? 'fa fa-stop' : 'fa fa-microphone',
    );
    this.onBusyChange(this.isBusy);
  }

  showError(message) {
    this.$errorText.text(message);
    this.$error.removeClass('d-none');
  }

  hideError() {
    this.$error.addClass('d-none');
    this.$errorText.text('');
  }

  // --------------------------------------------------------------- enhance

  async enhance() {
    if (this.isEnhancing || this.isDictating || !shouldEnableEnhance(this.draft)) return;
    const draft = this.draft;
    const requestId = ++this.enhanceRequestId;
    this.hideError();
    this.isEnhancing = true;
    this.syncState();

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ENHANCEMENT_TIMEOUT_MS);
    try {
      const response = await fetch('/langua/prompt_enhancement', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ draft, source: this.source }),
        signal: controller.signal,
      });
      const body = await response.json().catch(() => ({}));
      if (requestId !== this.enhanceRequestId) return;

      if (!response.ok) {
        // Localised copy first; the server's English `message` only for a code
        // this client does not know.
        const key = enhancementErrorKey(response.status, body.error);
        this.showError(key ? t(key) : (body.message || t('javascript.chat.enhance_error')));
        return;
      }
      const enhanced = String(body.enhanced_prompt || '').trim();
      if (!enhanced) {
        this.showError(t('javascript.chat.enhance_error'));
        return;
      }
      this.applyEnhancement(draft, enhanced);
    } catch (_error) {
      if (requestId !== this.enhanceRequestId) return;
      this.showError(t('javascript.chat.enhance_error'));
    } finally {
      clearTimeout(timer);
      if (requestId === this.enhanceRequestId) {
        this.isEnhancing = false;
        this.syncState();
      }
    }
  }

  // The enhanced text replaces the draft straight away (no confirmation);
  // the original stays one click away until the learner reverts, resets or
  // enhances again.
  applyEnhancement(original, enhanced) {
    this.originalDraft = original;
    this.setDraft(enhanced);
    this.$original.text(original).addClass('d-none');
    this.$toggleOriginal.text(t('javascript.chat.show_original'));
    this.$originalLinks.removeClass('d-none');
  }

  revertToOriginal() {
    if (!this.originalDraft) return;
    this.setDraft(this.originalDraft);
    this.clearOriginal();
  }

  clearOriginal() {
    this.originalDraft = '';
    this.$originalLinks.addClass('d-none');
    this.$original.addClass('d-none').text('');
    this.$toggleOriginal.text(t('javascript.chat.show_original'));
  }

  // --------------------------------------------------------------- dictate

  async startRecording() {
    if (this.isBusy) return;
    this.hideError();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.showError(t('javascript.chat.mic_capture_failed'));
      return;
    }

    // Busy from the first click: a second click while the permission prompt
    // is open must not start a parallel acquisition.
    const generation = this.dictationGeneration;
    this.isAcquiringMic = true;
    this.syncState();

    let stream = null;
    let failure = null;
    try {
      ({ stream, failure } = await acquireMicStream({ mediaDevices: navigator.mediaDevices }));
    } catch (error) {
      failure = { messageKey: 'javascript.chat.mic_capture_failed' };
    }

    if (generation !== this.dictationGeneration) {
      // Cancelled/reset while the browser was asking for permission.
      if (stream) stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.isAcquiringMic = false;

    if (!stream) {
      this.showError(t(failure.messageKey));
      this.syncState();
      return;
    }

    this.stream = stream;
    this.audioChunks = [];
    this.recorder = new AudioRecorder(stream, { sampleRate: 16000, channelCount: 1, bitsPerSample: 16 });
    this.recorder.addEventListener('dataavailable', (e) => {
      if (generation === this.dictationGeneration) this.audioChunks.push(e.data);
    });
    this.recorder.addEventListener('stop', () => this.onRecordingStopped(generation));
    this.recorder.start();
    this.isRecording = true;
    // Bound the session: auto-stop and transcribe what was captured.
    this.recordingTimer = setTimeout(() => this.stopRecording(), MAX_RECORDING_MS);
    this.syncState();
  }

  stopRecording() {
    if (!this.isRecording || !this.recorder) return;
    this.clearRecordingTimer();
    this.isRecording = false;
    this.isTranscribing = true;
    this.syncState();
    this.recorder.stop();
  }

  // Abandon everything dictation-related: pending permission prompt, live
  // recording, in-flight transcription. Nothing started before this call can
  // touch the textarea afterwards.
  cancelDictation() {
    this.dictationGeneration += 1;
    this.clearRecordingTimer();
    if (this.transcribeController) {
      this.transcribeController.abort();
      this.transcribeController = null;
    }
    if (this.recorder && this.isRecording) {
      try { this.recorder.stop(); } catch (_) { /* already stopped */ }
    }
    this.recorder = null;
    this.audioChunks = [];
    this.releaseStream();
    this.isAcquiringMic = false;
    this.isRecording = false;
    this.isTranscribing = false;
    this.syncState();
  }

  clearRecordingTimer() {
    if (this.recordingTimer) {
      clearTimeout(this.recordingTimer);
      this.recordingTimer = null;
    }
  }

  releaseStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  async onRecordingStopped(generation) {
    if (generation !== this.dictationGeneration) return; // cancelled
    this.releaseStream();
    const blob = new Blob(this.audioChunks, { type: 'audio/wav' });
    this.audioChunks = [];
    this.recorder = null;
    try {
      const text = await this.transcribe(blob);
      if (generation !== this.dictationGeneration) return; // cancelled mid-flight
      const next = appendTranscript(this.draft, text, PROMPT_MAX_CHARS);
      if (next !== this.draft) this.setDraft(next);
    } catch (_error) {
      if (generation !== this.dictationGeneration) return;
      this.showError(t('javascript.chat.dictate_error'));
    } finally {
      if (generation === this.dictationGeneration) {
        this.isTranscribing = false;
        this.syncState();
      }
    }
  }

  async transcribe(blob) {
    const formData = new FormData();
    formData.append('audio', blob, 'dictation.wav');
    formData.append('language', this.nativeLanguage);
    formData.append('model', 'scribe_v1');

    const controller = new AbortController();
    this.transcribeController = controller;
    const timer = setTimeout(() => controller.abort(), DICTATION_TIMEOUT_MS);
    try {
      const response = await fetch('/langua/stream_speech_to_text', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
          Accept: 'text/event-stream',
        },
        credentials: 'same-origin',
        body: formData,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Speech to text failed: ${response.status}`);
      const text = parseSseTranscript(await response.text());
      return text;
    } finally {
      clearTimeout(timer);
      if (this.transcribeController === controller) this.transcribeController = null;
    }
  }
}
